// src/pages/BlogArticle.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Avatar,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
} from "@mui/material";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";

const API_ROOT = "https://brokepiggytoken.xyz/wp-json/wp/v2/clblog";

export default function BlogArticle() {
  const { slug }            = useParams();
  const navigate            = useNavigate();

  const [post,  setPost]    = useState(null);
  const [more,  setMore]    = useState([]);   // side-panel posts
  const [load,  setLoad]    = useState(true);

  /* ───────────────────────────── fetch single post */
  useEffect(() => {
    const fetchPost = async () => {
      setLoad(true);
      try {
        const { data } = await axios.get(
          `${API_ROOT}?slug=${slug}&_embed&per_page=1`
        );
        if (!data.length) {
          navigate("/blog", { replace: true });
          return;
        }
        const p = data[0];

        /* hero image */
        const hero =
          p._embedded?.["wp:featuredmedia"]?.[0]?.source_url ?? "";

        /* category */
        const category =
          p._embedded?.["wp:term"]?.[0]?.[0]?.name ?? "Uncategorized";

        /* body: meta → editorHtml → plainField */
        const metaArr   = p.meta?._clblog_content || p.meta?.clblog_content || [];
        const metaBody  = Array.isArray(metaArr) && metaArr[0] ? metaArr[0] : "";
        const editorHtml= p.content?.rendered || "";
        const plain     = p.contentPlain || "";

        const toHtml = txt =>
          "<p>" +
          txt
            .replace(/\r\n|\r|\n/g, "\n")
            .split("\n\n")
            .map(s => s.replace(/\n/g, "<br/>"))
            .join("</p><p>") +
          "</p>";

        const htmlBody = metaBody ? toHtml(metaBody)
                      : editorHtml ? editorHtml
                      : plain      ? toHtml(plain)
                      : "<p></p>";

        const strip = s => s.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
        const excerpt = strip(htmlBody).slice(0, 160) + "…";

        setPost({
          id:        p.id,
          title:     p.title.rendered,
          html:      htmlBody,
          excerpt,
          date:      p.date,
          hero,
          category,
          authorName   : "CrowdLaunch Team",
          authorAvatar : "https://brokepiggytoken.xyz/wp-content/uploads/2025/04/crowdlaunch-lnch.png",
          url: `https://crowdlaunch.xyz/blog/${slug}`,
        });
      } catch (err) {
        console.error("Failed to fetch blog post:", err);
      } finally {
        setLoad(false);
      }
    };
    fetchPost();
  }, [slug, navigate]);

  /* ───────────────────────────── fetch “More Articles” once */
  useEffect(() => {
    const fetchMore = async () => {
      try {
        const { data } = await axios.get(`${API_ROOT}?_embed&per_page=6`);
        const mapped = data
          .filter(d => d.slug !== slug)          // exclude current article
          .slice(0, 4)                           // keep 4 suggestions
          .map(d => ({
            id       : d.id,
            slug     : d.slug,
            title    : d.title.rendered,
            hero     : d._embedded?.["wp:featuredmedia"]?.[0]?.source_url ?? "",
            category : d._embedded?.["wp:term"]?.[0]?.[0]?.name ?? "Uncategorized",
            date     : d.date,
          }));
        setMore(mapped);
      } catch (e) {
        console.warn("Could not load more posts:", e);
      }
    };
    fetchMore();
  }, [slug]);

  /* ───────────────────────────── skeleton / guard */
  if (load || !post) {
    return (
      <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
        <CircularProgress sx={{ color: "#FFD700" }} />
      </Box>
    );
  }

   <Helmet>
          <title>{`${post.title} | CrowdLaunch Blog`}</title>
          <meta
            name="description"
            content="Keep up with the latest articles in project tokenization, Web3, and decentralized finance."
          />
          <meta
            name="keywords"
            content="CrowdLaunch, CrowdLaunch Blog, Blog, DeFi Blog, project tokenization, web3, decentralized finance, startup funding, blockchain, crypto"
          />
          {/* Global Site Tag (gtag.js) - Google Analytics */}
          <script async src="https://www.googletagmanager.com/gtag/js?id=G-W97PQD73RV"></script>
          <script>
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-W97PQD73RV', { page_path: window.location.pathname });
            `}
          </script>
        </Helmet>
  /* ───────────────────────────── render */
  return (
    <>
      {/* ---------- SEO meta ---------- */}
      <Helmet>
        <title>{`${post.title} | CrowdLaunch Blog`}</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:type"        content="article" />
        <meta property="og:title"       content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        {post.hero && <meta property="og:image" content={post.hero} />}
        <meta property="og:url"         content={post.url} />
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        {post.hero && <meta name="twitter:image" content={post.hero} />}
      </Helmet>

      

      {/* ---------- Hero banner ---------- */}
      {post.hero && (
        <Box
          sx={{
            height: { xs: 220, md: 320 },
            backgroundImage: `url(${post.hero})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(0,0,0,0.45)",
            },
          }}
        />
      )}

      {/* ---------- Main layout (article + sidebar) ---------- */}
      <Container sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={4}>
          {/* ======== Article column ======== */}
          <Grid item xs={12} md={8}>
            {/* category + back link */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Chip
                label={post.category}
                size="small"
                sx={{
                  bgcolor: "#FFD700",
                  color: "#1C1E1E",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                }}
              />
              <Typography
                component={Link}
                to="/blog"
                sx={{
                  color: "#FFD700",
                  fontSize: "0.8rem",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                ← All Articles
              </Typography>
            </Box>

            {/* title */}
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                mb: 1,
                color: "#FFF",
                fontSize: { xs: "1.8rem", md: "2.4rem" },
              }}
            >
              {post.title}
            </Typography>

            {/* meta row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 4,
                color: "rgba(255,255,255,0.7)",
                fontSize: "0.9rem",
              }}
            >
              <Avatar
                src={post.authorAvatar}
                sx={{ width: 32, height: 32 }}
                alt={post.authorName}
              />
              <span>{post.authorName}</span>
              <Divider orientation="vertical" flexItem sx={{ bgcolor: "rgba(255,255,255,0.3)" }} />
              <span>{new Date(post.date).toLocaleDateString()}</span>
            </Box>

            {/* article body */}
            <Box
              sx={{
                color: "rgba(255,255,255,0.9)",
                "& h2, & h3, & h4": { color: "#FFD700", mt: 3, mb: 1.5 },
                "& p": { mb: 2, lineHeight: 1.7, fontSize: "1rem" },
                "& a": { color: "#FFD700" },
                "& img": { maxWidth: "100%", borderRadius: 2, my: 2 },
                "& pre": {
                  bgcolor: "#0E0E0F",
                  p: 2,
                  overflow: "auto",
                  borderRadius: 2,
                },
              }}
              dangerouslySetInnerHTML={{ __html: post.html }}
            />
          </Grid>

          {/* ======== Sidebar column ======== */}
          <Grid item xs={12} md={4}>
            <Typography
              variant="h6"
              sx={{ color: "#FFF", mb: 2, fontWeight: 700 }}
            >
              More Articles
            </Typography>

            {more.map(m => (
              <Card
                key={m.id}
                sx={{
                  mb: 2,
                  bgcolor: "#1A1A1D",
                  border: "1px solid rgba(255,255,255,0.06)",
                  "&:hover": { borderColor: "#FFD70066" },
                }}
              >
                <CardActionArea component={Link} to={`/blog/${m.slug}`}>
                  {m.hero && (
                    <CardMedia
                      component="img"
                      height="120"
                      image={m.hero}
                      alt={m.title}
                      sx={{ objectFit: "cover" }}
                    />
                  )}
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#FFF",
                        fontWeight: 600,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {m.title}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 0.5,
                      }}
                    >
                      <Chip
                        label={m.category}
                        size="small"
                        sx={{
                          bgcolor: "#FFD700",
                          color: "#1C1E1E",
                          fontSize: "0.6rem",
                        }}
                      />
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                        {new Date(m.date).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
